import cookieService from './cookieService';
import { CONSTANTS } from '../configs/constants';
import toast from 'react-hot-toast';
import { clearPinnedProjects } from '../utils/activeProject';
import { clearLogoutSnapshot, markEverLoggedIn } from '../utils/userPrefs';

// Decode JWT payload (no verification — we only need the user id to scope cleanup).
const userIdFromToken = (token) => {
    if (!token || typeof token !== 'string') return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload?.sub || null;
    } catch (_) {
        return null;
    }
};

// The trusted-device cookie holds a { emailLowercased: token } map so one browser can remember
// several accounts independently. A single shared value meant verifying account B overwrote
// account A's trust, forcing A to re-verify on the next switch (and vice versa). Parse
// defensively: a missing cookie, malformed JSON, or the old plain-string format all read as
// "no trusted token", which just costs one extra 2FA rather than crashing.
const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const readDeviceTokenMap = () => {
    const raw = cookieService.getCookie(CONSTANTS.device_token);
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
        return {};
    }
};

export const setSession = ({ user, tokens, deviceToken }) => {
    toast.success(`Welcome ${user.firstname}!`);
    markEverLoggedIn();
    cookieService.setCookie(CONSTANTS.access_token, tokens.access.token);
    if (tokens.refresh && tokens.refresh.token) {
        cookieService.setCookie(CONSTANTS.refresh_token, tokens.refresh.token);
    }
    // Only present right after a 2FA code was verified. Stored in a per-account map (keyed by
    // email) so trusting this account doesn't clobber another account already trusted in this
    // browser. Persists 90 days (unlike the session-only cookies above) so this device can skip
    // 2FA until it expires — see backend tokenService.TRUSTED_DEVICE_EXPIRATION_DAYS. The backend
    // still enforces each token's own 90-day expiry, so rewriting the cookie can't extend trust
    // past what the server allows.
    if (deviceToken && user && user.email) {
        const deviceTokenMap = readDeviceTokenMap();
        deviceTokenMap[normalizeEmail(user.email)] = deviceToken;
        cookieService.setCookie(CONSTANTS.device_token, JSON.stringify(deviceTokenMap), 90);
    }
    // Clear ONLY this user's pinned-project bucket — calling clearPinnedProjects()
    // with no userId previously wiped every account on the device, so on shared
    // machines other users lost their sidebar pins after any login.
    if (user && user.id) {
        clearPinnedProjects(user.id);
    }
};

export const clearSession = () => {
    cookieService.deleteCookie(CONSTANTS.access_token);
    cookieService.deleteCookie(CONSTANTS.refresh_token);
    // device_token is deliberately left alone — logging out shouldn't forget a trusted device,
    // only its own 90-day expiry (or a wiped browser) should.
    // No userId available here — leave per-user buckets intact; the user's pins
    // come back if they log in again on this device.
};

export const softLogout = () => {
    // Token kick (both access and refresh rejected/exhausted). Wipe the route snapshot so
    // the next login lands on /dashboard instead of restoring the page that just rejected
    // the user.
    const token = cookieService.getCookie(CONSTANTS.access_token);
    const userId = userIdFromToken(token);
    if (userId) clearLogoutSnapshot(userId);
    cookieService.deleteCookie(CONSTANTS.access_token);
    cookieService.deleteCookie(CONSTANTS.refresh_token);
};

export const getAccessToken = () => {
    return cookieService.getCookie(CONSTANTS.access_token);
};

export const getRefreshToken = () => {
    return cookieService.getCookie(CONSTANTS.refresh_token);
};

// Sent with every login attempt so the backend can recognize this device as already
// 2FA-verified FOR THIS ACCOUNT. Undefined when this account has never completed a code on this
// device (or its token expired) — the backend treats that the same as a device it's never seen.
export const getDeviceToken = (email) => {
    const token = readDeviceTokenMap()[normalizeEmail(email)];
    return token || undefined;
};

// Updates just the access/refresh cookies after a successful silent refresh — no toast, no
// pinned-project reset, unlike setSession() which only runs on an actual login.
export const setRefreshedTokens = (tokens) => {
    if (tokens && tokens.access && tokens.access.token) {
        cookieService.setCookie(CONSTANTS.access_token, tokens.access.token);
    }
    if (tokens && tokens.refresh && tokens.refresh.token) {
        cookieService.setCookie(CONSTANTS.refresh_token, tokens.refresh.token);
    }
};
